import { GetStaticPaths, GetStaticProps } from "next"
import Image from "next/image"
import { Box, Container } from "@mui/material"

import { makeStyles } from "makeStyles"

import SEO from "components/SEO"
import Link from "components/i18n/Link"
import Button from "components/Buttons/Button"

import fetchCategory, {
	fetchCategorySeries,
	fetchCategorySlugs,
	SeriesPreview as SeriesPreviewI,
} from "utils/commerceTools/fetchCategory"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import { supportedValidLocales } from "config/i18n"
import serializeJSON from "@secureo/common/utils/serializeJSON"
import useProductHitImpressionTracking from "@secureo/common/hooks/tracking/useProductHitImpressionTracking"

import { Category } from "@secureo/common/typings/Category"

import { trackGA4SelectItem } from "../../../common/utils/tracking/productHit"

const useStyles = makeStyles()((theme) => ({
	wrapper: {
		backgroundColor: theme.palette.secondary.light,
		boxShadow: "0 10px 15px rgba(0, 0, 0, 0.2)",
		padding: 30,
		marginBottom: 50,
		borderRadius: 5,
		"@media only screen and (max-width: 960px)": {
			padding: "10px 0px 0px 0px",
			background: "none",
			boxShadow: "0 10px 15px rgba(0, 0, 0, 0.3)",
		},
	},
	item: {
		color: "#fff",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		width: "100%",
		"@media only screen and (max-width: 960px)": {
			position: "relative",
			flexDirection: "column-reverse",
		},
	},
	textColumn: {
		width: "70%",
		background: "none",
		"@media only screen and (max-width: 960px)": {
			width: "100%",
			backgroundColor: theme.palette.secondary.light,
			borderRadius: 5,
			zIndex: 0,
		},
	},
	mobileBackground: {
		background: "none",
		"@media only screen and (max-width: 960px)": {
			backgroundColor: theme.palette.secondary.light,
			padding: "60px 20px 20px 20px",
			borderBottomLeftRadius: 5,
			borderBottomRightRadius: 5,
		},
	},
	imageColumn: {
		position: "relative",
		height: 250,
		width: "30%",
		"@media only screen and (max-width: 960px)": {
			width: "100%",
			maxWidth: 300,
			marginBottom: -70,
		},
	},
	image: {
		width: "100%",
		height: "100%",
	},
	headline: {
		fontSize: 22,
		"@media only screen and (max-width: 960px)": {
			fontSize: 16,
		},
	},
	description: {
		fontSize: "1rem",
		marginBottom: 25,
		"@media only screen and (max-width: 960px)": {
			fontSize: "0.9rem",
			marginBottom: 25,
		},
	},
	buttonWidth: {
		width: 220,
		"@media only screen and (max-width: 960px)": {
			width: "100%",
		},
	},
	hideOnMobile: {
		display: "block",
		"@media only screen and (max-width: 960px)": {
			display: "none",
		},
	},
	hideOnDesktop: {
		display: "none",
		"@media only screen and (max-width: 960px)": {
			display: "block",
		},
	},
}))

const SeriesPreview = ({ series, position, categoryName }: SeriesPreviewProps) => {
	const { classes } = useStyles()
	const { name, slug, image, shortDescription } = series

	const trackImpression = useProductHitImpressionTracking(
		{ name, manufacturer: "Secureo", grossPriceCents: 0 },
		categoryName,
		categoryName,
		position,
	)

	return (
		<Link
			key={slug}
			href={`/s/${slug}`}
			onClick={() =>
				trackGA4SelectItem(name, 0, "Secureo", categoryName, categoryName, position)
			}
		>
			<div className={classes.wrapper}>
				<div className={classes.item} style={{ scrollSnapAlign: "start" }}>
					<div className={classes.textColumn}>
						<div className={classes.mobileBackground}>
							<h2 className={classes.headline}>{name}</h2>
							<Box className={classes.description}>
								<div
									className={classes.hideOnMobile}
									dangerouslySetInnerHTML={{ __html: shortDescription }}
								/>
								<div
									className={classes.hideOnDesktop}
									dangerouslySetInnerHTML={{
										__html: shortDescription,
									}}
								/>
							</Box>
							<div className={classes.buttonWidth}>
								<Button color="primary">Mehr erfahren</Button>
							</div>
						</div>
					</div>
					{image && (
						<div className={classes.imageColumn}>
							<div className={classes.image}>
								<Image
									alt={name}
									src={image}
									quality={80}
									fill
									sizes="100vw"
									style={{
										objectFit: "contain",
									}}
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</Link>
	)
}

interface SeriesPreviewProps {
	series: SeriesPreviewI
	categoryName: string
	position: number
}

const CategoryPage = (props: Props) => {
	const { category, series } = props
	const { slug, name, metaTitle, metaDescription, shortDescription } = category

	const asPath = `/c/${slug}`

	return (
		<div
			style={{
				background: "#182F37",
				color: "white",
			}}
		>
			<Box py={5}>
				<SEO
					asPath={asPath}
					pageType="category"
					categoryName={name}
					title={metaTitle}
					description={metaDescription}
				/>
				<Container>
					<h1>{name}</h1>
					<p
						style={{ marginBottom: "3rem" }}
						dangerouslySetInnerHTML={{ __html: shortDescription }}
					/>
					<div>
						{series.map((series, i) => {
							return (
								<SeriesPreview
									key={series.slug}
									series={series}
									categoryName={name}
									position={i}
								/>
							)
						})}
					</div>
				</Container>
			</Box>
		</div>
	)
}

export const getStaticPaths: GetStaticPaths = async () => {
	const accessToken = await anonymousAccessTokenCache()
	const slugs = await fetchCategorySlugs(accessToken)
	const filteredSlugs = slugs.filter((slug) => !["schloesser"].includes(slug))

	const paths = []
	for (const slug of filteredSlugs) {
		for (const locale of supportedValidLocales) {
			paths.push({ params: { slug }, locale })
		}
	}

	return {
		paths,
		fallback: false,
	}
}

export const getStaticProps: GetStaticProps = async (context) => {
	const accessToken = await anonymousAccessTokenCache()
	const locale = context.locale as string
	const slug = context.params?.slug as string

	const category = await fetchCategory(accessToken, slug, locale)

	if (!category) {
		return {
			notFound: true,
			props: {},
		}
	}

	const series = await fetchCategorySeries(accessToken, category.id)

	const props = {
		locale,
		category,
		series,
	}

	return serializeJSON({ props, revalidate: 10 })
}

interface Props {
	locale: string
	category: Category
	series: SeriesPreviewI[]
}

export default CategoryPage
